import React, { useEffect, useState, useRef } from "react";
import { apiRequest } from "../utils/api";
import { useAuthContext } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import useDebouncedEffect from "../hooks/useDebouncedEffect";
import SalesItemCard from "../components/SalesItemCard";
import { useScrollPersistence } from "../hooks/useScrollPersistence";
import "../styles/SalesSummaryPage.css";

const SalesSummaryPage = () => {
  const [items, setItems] = useState([]);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortKey, setSortKey] = useState("sold_quantity");
  const [sortPeriod, setSortPeriod] = useState("3month");
  const [sortOrder, setSortOrder] = useState("desc");
  const [categoryId, setCategoryId] = useState("");
  const [filterMode, setFilterMode] = useState("only_sold");
  const [overstockMode, setOverstockMode] = useState("");
  const [categories, setCategories] = useState([]);
  const [titleKeyword, setTitleKeyword] = useState("");
  const [debouncedTitleKeyword, setDebouncedTitleKeyword] = useState("");
  const [salesTrend, setSalesTrend] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const { user: userInfo } = useAuthContext();
  const [storeId, setStoreId] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;
  const [queryReady, setQueryReady] = useState(false);
  const [editedValues, setEditedValues] = useState({});

  const salesListScrollRef = useRef(null);
  const { saveScroll } = useScrollPersistence(
    salesListScrollRef,
    "sales_summary_scroll",
    items.length
  );
 
  const didApplyQueryParams = useRef(false);
  const requestControllerRef = useRef(null);
  const categoryControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      if (categoryControllerRef.current) {
        categoryControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (userInfo?.store_id) {
      setStoreId(String(userInfo.store_id));
    }
  }, [userInfo]);

  useEffect(() => {
    if (didApplyQueryParams.current) return;

    const paramCategoryId = searchParams.get("category_id") || "";
    const page = parseInt(searchParams.get("page") || "1", 10);
    const filter = searchParams.get("filter_mode") || "only_sold";
    const sk = searchParams.get("sortKey") || "sold_quantity";
    const sp = searchParams.get("sortPeriod") || "3month";
    const so = searchParams.get("sortOrder") || "desc";
    const title = searchParams.get("title") || "";
    const custom_start = searchParams.get("custom_start") || "";
    const custom_end = searchParams.get("custom_end") || "";
    const sales_trend = searchParams.get("sales_trend") || "";
    const overstock_mode = searchParams.get("overstock_mode") || "";

    const year = searchParams.get("year");
    const month = searchParams.get("month");
    const day = searchParams.get("day");

    setCategoryId(paramCategoryId);
    setFilterMode(filter);
    setSortKey(sk);
    setSortPeriod(sp);
    setSortOrder(so);
    setTitleKeyword(title);
    setDebouncedTitleKeyword(title);
    setSalesTrend(sales_trend);
    setOverstockMode(overstock_mode);
    setCurrentPage(Number.isNaN(page) ? 1 : page);

    if (custom_start && custom_end) {
      setCustomStart(custom_start);
      setCustomEnd(custom_end);
      setSortPeriod("custom");
    } else if (year && month && day) {
      const dateStr = `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      setCustomStart(dateStr);
      setCustomEnd(dateStr);
      setSortPeriod("custom");
    }

    didApplyQueryParams.current = true;
    setQueryReady(true);
  }, [searchParams]);

  useDebouncedEffect(() => {
    setDebouncedTitleKeyword(titleKeyword);
  }, [titleKeyword], 250);

  useEffect(() => {
    if (!didApplyQueryParams.current) return;

    const params = new URLSearchParams();

    params.set("page", String(currentPage));
    params.set("filter_mode", filterMode);
    params.set("sortKey", sortKey);
    params.set("sortPeriod", sortPeriod);
    params.set("sortOrder", sortOrder);

    if (categoryId) {
      params.set("category_id", categoryId);
    }

    if (debouncedTitleKeyword) {
      params.set("title", debouncedTitleKeyword);
    }

    if (overstockMode) {
      params.set("overstock_mode", overstockMode);
    }

    if (salesTrend) {
      params.set("sales_trend", salesTrend);
    }

    if (sortPeriod === "custom") {
      if (customStart) {
        params.set("custom_start", customStart);
      }

      if (customEnd) {
        params.set("custom_end", customEnd);
      }
    }

    setSearchParams(params, { replace: true });
  }, [
    currentPage,
    categoryId,
    filterMode,
    overstockMode,
    salesTrend,
    sortKey,
    sortPeriod,
    sortOrder,
    customStart,
    customEnd,
    debouncedTitleKeyword,
    setSearchParams,
  ]);

  useEffect(() => {
    if (categoryControllerRef.current) {
      categoryControllerRef.current.abort();
    }

    const controller = new AbortController();
    categoryControllerRef.current = controller;

    const fetchCategories = async () => {
      try {
        const res = await apiRequest(
          "inventory/categorys/",
          "GET",
          null,
          {},
          true,
          controller.signal
        );
        setCategories(res);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("カテゴリ取得失敗:", err);
      }
    };

    fetchCategories();

    return () => {
      controller.abort();
    };
  }, []);

  const fetchData = async (page = 1) => {
    if (!queryReady) return;
    if (!storeId) return;
    if (sortPeriod === "custom" && (!customStart || !customEnd)) return;

    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }

    const controller = new AbortController();
    requestControllerRef.current = controller;

    const orderingKey =
      sortKey === "sold_quantity"
        ? `sales_summary__${sortPeriod}`
        : sortKey === "total_purchased"
        ? `purchase_summary__${sortPeriod}`
        : "stock";

    const params = {
      store_id: storeId,
      page,
      selected_period: sortPeriod,
      ordering: sortOrder === "desc" ? `-${orderingKey}` : orderingKey,
    };

    if (sortPeriod === "custom" && customStart && customEnd) {
      params.custom_start = customStart;
      params.custom_end = customEnd;
    }

    if (categoryId) params.category_id = categoryId;
    if (filterMode) params.filter_mode = filterMode;
    if (debouncedTitleKeyword) params.title = debouncedTitleKeyword;
    if (overstockMode) params.overstock_mode = overstockMode;
    if (salesTrend) params.sales_trend = salesTrend;

    try {
      const res = await apiRequest(
        `inventory/sales/summary/?${new URLSearchParams(params)}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      setItems(res.results ?? []);
      setCurrentPage(page);
      setTotalPages(Math.max(1, Math.ceil((res.count ?? 0) / pageSize)));
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("取得失敗:", err);
    }
  };

  useEffect(() => {
    if (!queryReady) return;

    fetchData(currentPage);
  }, [
    queryReady,
    currentPage,
    storeId,
    categoryId,
    filterMode,
    overstockMode,
    salesTrend,
    sortKey,
    sortPeriod,
    sortOrder,
    customStart,
    customEnd,
    debouncedTitleKeyword,
  ]);

  const handleFieldChange = (inventoryId, field, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.inventory_id === inventoryId ? { ...item, [field]: value } : item
      )
    );

    setEditedValues((prev) => ({
      ...prev,
      [inventoryId]: {
        ...(prev[inventoryId] || {}),
        [field]: value,
      },
    }));
  };

  useDebouncedEffect(() => {
    const updateAll = async () => {
      const updates = { ...editedValues };
      if (Object.keys(updates).length === 0) return;

      const failedUpdates = {};
      setEditedValues({});

      for (const [inventoryId, fields] of Object.entries(updates)) {
        try {
          const updated = await apiRequest(
            `inventory/store_inventory/${inventoryId}/update/`,
            "PATCH",
            {
              store_id: storeId,
              ...fields,
            }
          );

          setItems((prev) =>
            prev.map((item) =>
              String(item.inventory_id) === String(inventoryId)
                ? {
                    ...item,
                    ...updated,
                  }
                : item
            )
          );
        } catch (err) {
          console.error(`更新失敗: ${inventoryId}`, err);
          failedUpdates[inventoryId] = fields;
        }
      }

      if (Object.keys(failedUpdates).length > 0) {
        setEditedValues((prev) => ({
          ...failedUpdates,
          ...prev,
        }));
      }
    };

    if (Object.keys(editedValues).length > 0) {
      updateAll();
    }
  }, [editedValues, storeId], 1000);

  const changePage = (page) => {
    setCurrentPage(page);

    requestAnimationFrame(() => {
      if (salesListScrollRef.current) {
        salesListScrollRef.current.scrollTop = 0;
      }
    });
  };

  return (
    <div className="SalesSummaryPage-container">
      <div className="SalesSummaryPage-filters">
        <div className="SalesSummaryPage-extra-filters">
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">全カテゴリー</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.category_name}
              </option>
            ))}
          </select>

          <select
            value={filterMode}
            onChange={(e) => {
              setFilterMode(e.target.value);
              setCurrentPage(1);
            }}
          > 
            <option value="only_sold">売買履歴ありのみ</option>
            <option value="only_sold_zero">売買履歴あり・在庫0</option>
            <option value="exclude_empty">売買なし・在庫あり</option>
            <option value="only_empty">売買なし・在庫なし</option>
            <option value="">すべて表示</option>
          </select>

          <input
            type="text"
            value={titleKeyword}
            onChange={(e) => {
              setTitleKeyword(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="作品で絞り込み"
          />
        </div>

        <div className="SalesSummaryPage-sort">
          <label>並び替え:</label>

          <button
            onClick={() => {
              const today = new Date().toISOString().slice(0, 10);
              setCustomStart(today);
              setCustomEnd(today);
              setSortPeriod("custom");
            }}
          >
            今日
          </button>

          <button
            onClick={() => {
              const d = new Date();
              d.setDate(d.getDate() - 1);
              const yesterday = d.toISOString().slice(0, 10);
              setCustomStart(yesterday);
              setCustomEnd(yesterday);
              setSortPeriod("custom");
            }}
          >
            昨日
          </button>
          <select
            value={sortKey}
            onChange={(e) => {
              setSortKey(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="sold_quantity">販売数</option>
            <option value="total_purchased">仕入数</option>
            <option value="stock">在庫</option>
          </select>

          <select 
            value={sortPeriod} 
            onChange={(e) => {
              setSortPeriod(e.target.value);
              setCurrentPage(1);
            }}>
            <option value="1week">1週間</option>
            <option value="1month">1ヶ月</option>
            <option value="3month">3ヶ月</option>
            <option value="6month">6ヶ月</option>
            <option value="12month">12ヶ月</option>
            <option value="custom">カスタム</option>
          </select>

          {sortPeriod === "custom" && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span>～</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </>
          )}

          <select value={sortOrder} onChange={(e) => {
            setSortOrder(e.target.value);
            setCurrentPage(1);
            }}>
            <option value="desc">降順</option>
            <option value="asc">昇順</option>
          </select>
        </div>

        <div className="SalesSummaryPage-extra-filters">
          <label>3ヶ月分以上あり:</label>
          <select value={overstockMode} onChange={(e) => {
            setOverstockMode(e.target.value);
            setCurrentPage(1);
            }}>
            <option value="">過剰在庫フィルターなし</option>
            <option value="6month">⚠ 6ヶ月ベース</option>
            <option value="12month">⚠ 12ヶ月ベース</option>
          </select>
        </div>

        <select value={salesTrend} onChange={(e) => {
          setSalesTrend(e.target.value);
          setCurrentPage(1);
          }}>
          <option value="">売上変化フィルターなし</option>
          <option value="increase_15">⚡ 直近15日で増加</option>
          <option value="decrease_15">📉 直近15日で減少</option>
          <option value="increase_30">⚡ 直近30日で増加</option>
          <option value="decrease_30">📉 直近30日で減少</option>
        </select>
      </div>

      <div
        ref={salesListScrollRef}
        className="SalesSummaryPage-listbox"
      >
        {items.map((item) => (
          <div
            className="SalesSummaryPage-list"
            key={item.inventory_id}
            data-id={item.inventory_id}
          >
            <SalesItemCard
              item={item}
              onChange={handleFieldChange}
              storeId={storeId}
              categoryId={categoryId}
              currentPage={currentPage}
              filterMode={filterMode}
              sortPeriod={sortPeriod}
              sortOrder={sortOrder}
              titleKeyword={debouncedTitleKeyword}
              customStart={customStart}
              customEnd={customEnd}
              onBeforeDetail={saveScroll}
            />
          </div>
        ))}
      </div>

      <div className="pagination-controls">
        <button
          disabled={currentPage === 1}
          onClick={() => changePage(currentPage - 1)}
        >
          ← 前
        </button>

        <span>ページ {currentPage} / {totalPages}</span>

        <button
          disabled={currentPage >= totalPages}
          onClick={() => changePage(currentPage + 1)}
        >
          次 →
        </button>
      </div>
    </div>
  );
};

export default SalesSummaryPage;